import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    ArrowLeft, Camera, CheckCircle, Loader2, Upload, X,
    AlertTriangle, Gauge, ChevronDown, Receipt, Plus,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import {
    enqueueJobPhoto, enqueueMeterReading, enqueueFuelLog,
    enqueueViaje, enqueueIdleToggle, enqueueTollCreate, broadcastPending,
} from "../../lib/offlineQueue";
import dict from "../../i18n/es-MX";
import ErrorBoundary from "../../components/ErrorBoundary";

const fmtDateTime = (iso) => iso
    ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

function Section({ title, defaultOpen = true, children, badge }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <section className="rounded-2xl bg-slate-900/60 border border-slate-800 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
                <div className="flex items-center gap-2">
                    <h2 className="font-display text-sm uppercase tracking-wider font-bold">{title}</h2>
                    {badge}
                </div>
                <ChevronDown size={18} className={`text-slate-500 transition ${open ? "rotate-180" : ""}`}/>
            </button>
            {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
        </section>
    );
}

function PhotoTile({ entry, moment, asset, jobId, onChange }) {
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");
    const done = moment === "antes" ? entry.antes_done : entry.despues_done;

    const onFile = async (file) => {
        if (!file) return;
        setUploading(true); setErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("asset_id", asset.asset_id);
            fd.append("moment", moment);
            fd.append("checklist_key", entry.key);
            await api.post(`/jobs/${jobId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange();
        } catch (e) {
            if (!e?.response) {
                await enqueueJobPhoto({
                    blob: file, filename: file.name || "photo.jpg",
                    contentType: file.type || "image/jpeg",
                    jobId, assetId: asset.asset_id, moment, checklistKey: entry.key,
                });
                await broadcastPending();
                setErr(dict.fieldOps.queuedOffline);
            } else {
                setErr(formatApiError(e));
            }
        }
        finally { setUploading(false); }
    };
    const inputId = `${moment}-${asset.asset_id}-${entry.key}`;

    return (
        <div
            data-testid={`photo-tile-${moment}-${entry.key}`}
            className={`rounded-xl border-2 p-3 transition ${done ? "border-emerald-500 bg-emerald-500/5" : entry.required ? "border-brand-orange/50 bg-slate-950/60" : "border-slate-700 bg-slate-950/40"}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-100 truncate">{entry.label_es}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                        {entry.required ? dict.fieldOps.required : dict.fieldOps.optional}
                    </div>
                </div>
                {done && <CheckCircle size={18} className="text-emerald-400 flex-shrink-0"/>}
            </div>
            <div className="flex gap-2 mt-3">
                <label htmlFor={`${inputId}-cam`} className="flex-1 inline-flex items-center justify-center gap-1 h-11 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase text-xs cursor-pointer active:scale-95 transition">
                    {uploading ? <Loader2 className="animate-spin" size={14}/> : <Camera size={14}/>}
                    {done ? dict.fieldOps.retake : dict.fieldOps.take}
                </label>
                <input id={`${inputId}-cam`} type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])} className="hidden"/>
                <label htmlFor={`${inputId}-upl`} className="inline-flex items-center justify-center gap-1 h-11 px-3 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-300 cursor-pointer">
                    <Upload size={14}/>
                </label>
                <input id={`${inputId}-upl`} type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} className="hidden"/>
            </div>
            {err && <div className="mt-2 text-xs text-red-300">{err}</div>}
        </div>
    );
}

function PhotoSection({ asset, moment, jobId, onChange }) {
    const required = asset.entries.filter(e => e.required);
    const optional = asset.entries.filter(e => !e.required);
    const ready = moment === "antes" ? asset.antes_ready : asset.despues_ready;
    const done = required.filter(e => moment === "antes" ? e.antes_done : e.despues_done).length;

    return (
        <div className="space-y-3" data-testid={`photo-section-${moment}-${asset.asset_id}`}>
            <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{asset.asset_nickname}</div>
                <div className={`text-xs font-mono ${ready ? "text-emerald-400" : "text-brand-orange"}`}>
                    {done}/{required.length}
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
                {[...required, ...optional].map((e) => (
                    <PhotoTile
                        key={`${moment}-${asset.asset_id}-${e.key}`}
                        entry={e} moment={moment} asset={asset} jobId={jobId}
                        onChange={onChange}
                    />
                ))}
            </div>
        </div>
    );
}

function MeterCard({ asset, jobId, onChange }) {
    const [last, setLast] = useState({});
    const [kind, setKind] = useState("hour_meter_horas");
    const [value, setValue] = useState("");
    const [file, setFile] = useState(null);
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const loadLast = useCallback(async () => {
        try {
            const r = await api.get(`/assets/${asset.asset_id}/last-meter-reading`);
            setLast(r.data);
        } catch (e) { /* silent */ }
    }, [asset.asset_id]);
    useEffect(() => { loadLast(); }, [loadLast]);

    const prev = last[kind];
    const submit = async () => {
        setErr("");
        const v = parseFloat(value);
        if (!v || v < 0) { setErr(dict.fieldOps.invalidReading); return; }
        if (prev && v < prev.value) {
            setErr(`${dict.fieldOps.readingLowerThan} ${prev.value}`);
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            if (file) fd.append("file", file);
            fd.append("asset_id", asset.asset_id);
            fd.append("job_id", jobId);
            fd.append("kind", kind);
            fd.append("value", String(v));
            await api.post("/meter-readings", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setValue(""); setFile(null);
            loadLast();
            onChange();
        } catch (e) {
            if (!e?.response) {
                await enqueueMeterReading({
                    blob: file || null, filename: file?.name, contentType: file?.type,
                    assetId: asset.asset_id, jobId, kindOfMeter: kind, value: v,
                });
                await broadcastPending();
                setValue(""); setFile(null);
                setErr(dict.fieldOps.queuedOffline);
            } else {
                setErr(formatApiError(e));
            }
        }
        finally { setSubmitting(false); }
    };

    return (
        <div className="rounded-xl bg-slate-950/60 border border-slate-800 p-4 space-y-3" data-testid={`meter-card-${asset.asset_id}`}>
            <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">{asset.asset_nickname}</div>
                <Gauge size={18} className="text-brand-orange"/>
            </div>
            <div className="flex gap-2">
                {[["hour_meter_horas", "Horas"], ["odometer_km", "Km"]].map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setKind(k)} data-testid={`meter-kind-${asset.asset_id}-${k}`}
                        className={`flex-1 h-9 rounded-lg border text-xs font-semibold uppercase ${kind===k?"bg-brand-orange text-slate-950 border-brand-orange":"border-slate-700 text-slate-300"}`}>
                        {label}
                    </button>
                ))}
            </div>
            <div className="text-xs text-slate-400">
                {dict.fieldOps.lastReading}: <span className="text-slate-200">{prev ? `${prev.value} (${fmtDateTime(prev.taken_at)})` : "—"}</span>
            </div>
            <input
                type="number" value={value} onChange={(e) => setValue(e.target.value)}
                placeholder={dict.fieldOps.enterValue} data-testid={`meter-value-${asset.asset_id}`}
                className="w-full h-12 px-3 rounded-lg bg-slate-900 border border-slate-700 text-base"
            />
            <div className="flex gap-2">
                <label className="flex-1 inline-flex items-center justify-center gap-1 h-11 rounded-lg border-2 border-dashed border-slate-700 hover:border-brand-orange text-slate-300 text-xs uppercase cursor-pointer">
                    <Camera size={14}/>
                    {file ? file.name.slice(0,12)+"…" : dict.fieldOps.photoOfMeter}
                    <input type="file" accept="image/*" capture="environment" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" data-testid={`meter-photo-${asset.asset_id}`}/>
                </label>
            </div>
            {err && <div className="text-xs text-red-300" data-testid="meter-err">{err}</div>}
            <button
                type="button" onClick={submit} disabled={submitting || !value}
                data-testid={`meter-submit-${asset.asset_id}`}
                className="w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold uppercase tracking-wider text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
                {submitting && <Loader2 className="animate-spin" size={16}/>}
                {dict.fieldOps.saveReading}
            </button>
        </div>
    );
}

function FuelSection({ assets, jobId }) {
    const [assetId, setAssetId] = useState(assets[0]?.asset_id || "");
    const [form, setForm] = useState({ date: new Date().toISOString().slice(0,10), liters:"", total_cost_mxn:"", hour_meter_in:"", hour_meter_out:"" });
    const [file, setFile] = useState(null);
    const [logs, setLogs] = useState([]);
    const [err, setErr] = useState(""); const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try { const r = await api.get("/fuel-logs", { params: { job_id: jobId } }); setLogs(r.data); } catch {}
    }, [jobId]);
    useEffect(() => { load(); }, [load]);

    const liters = parseFloat(form.liters) || 0;
    const cost = parseFloat(form.total_cost_mxn) || 0;
    const hin = parseFloat(form.hour_meter_in); const hout = parseFloat(form.hour_meter_out);
    const live = useMemo(() => {
        const r = { cost_per_liter: liters ? (cost / liters).toFixed(2) : "—" };
        if (!isNaN(hin) && !isNaN(hout) && hout > hin && liters) {
            const hrs = hout - hin; const lph = liters / hrs;
            r.lph = lph.toFixed(2);
            r.anomaly = lph < 5 || lph > 30;
        }
        return r;
    }, [liters, cost, hin, hout]);

    const submit = async () => {
        if (!assetId) { setErr(dict.fieldOps.pickAsset); return; }
        if (!liters || !cost) { setErr(dict.fieldOps.fuelRequired); return; }
        setSubmitting(true); setErr("");
        try {
            const fd = new FormData();
            if (file) fd.append("file", file);
            fd.append("asset_id", assetId); fd.append("job_id", jobId);
            fd.append("date", form.date); fd.append("liters", String(liters));
            fd.append("total_cost_mxn", String(cost));
            if (form.hour_meter_in) fd.append("hour_meter_in", form.hour_meter_in);
            if (form.hour_meter_out) fd.append("hour_meter_out", form.hour_meter_out);
            await api.post("/fuel-logs", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm({ ...form, liters:"", total_cost_mxn:"", hour_meter_in:"", hour_meter_out:"" }); setFile(null);
            load();
        } catch (e) {
            if (!e?.response) {
                await enqueueFuelLog({
                    blob: file || null, filename: file?.name, contentType: file?.type,
                    assetId, jobId, date: form.date, liters, total_cost_mxn: cost,
                    hour_meter_in: form.hour_meter_in ? parseFloat(form.hour_meter_in) : null,
                    hour_meter_out: form.hour_meter_out ? parseFloat(form.hour_meter_out) : null,
                });
                await broadcastPending();
                setForm({ ...form, liters:"", total_cost_mxn:"", hour_meter_in:"", hour_meter_out:"" }); setFile(null);
                setErr(dict.fieldOps.queuedOffline);
            } else {
                setErr(formatApiError(e));
            }
        }
        finally { setSubmitting(false); }
    };

    return <div className="space-y-3" data-testid="fuel-section">
        <select value={assetId} onChange={(e)=>setAssetId(e.target.value)} data-testid="fuel-asset" className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm">
            {assets.map(a => <option key={a.asset_id} value={a.asset_id}>{a.asset_nickname}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2">
            <input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="fuel-date"/>
            <input type="number" placeholder={dict.fieldOps.liters} value={form.liters} onChange={e=>setForm({...form,liters:e.target.value})} className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="fuel-liters"/>
            <input type="number" placeholder={dict.fieldOps.totalCost} value={form.total_cost_mxn} onChange={e=>setForm({...form,total_cost_mxn:e.target.value})} className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="fuel-cost"/>
            <input type="number" placeholder={dict.fieldOps.hoursIn} value={form.hour_meter_in} onChange={e=>setForm({...form,hour_meter_in:e.target.value})} className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="fuel-hours-in"/>
            <input type="number" placeholder={dict.fieldOps.hoursOut} value={form.hour_meter_out} onChange={e=>setForm({...form,hour_meter_out:e.target.value})} className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="fuel-hours-out"/>
            <label className="flex items-center gap-2 h-10 px-3 rounded-lg border-2 border-dashed border-slate-700 text-xs text-slate-400 cursor-pointer">
                <Upload size={12}/>{file ? file.name.slice(0,10)+"…" : dict.fieldOps.ticketPhoto}
                <input type="file" accept="image/*" onChange={e=>setFile(e.target.files?.[0])} className="hidden"/>
            </label>
        </div>
        <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-2 text-xs">
            <div>$/L: <span className="text-brand-orange font-mono">{live.cost_per_liter}</span></div>
            {live.lph && <div>L/h: <span className={live.anomaly ? "text-red-400 font-mono" : "text-emerald-400 font-mono"}>{live.lph}</span>{live.anomaly && <span className="ml-1 text-red-300">⚠ {dict.fieldOps.outOfRange}</span>}</div>}
        </div>
        {err && <div className="text-xs text-red-300" data-testid="fuel-err">{err}</div>}
        <button type="button" onClick={submit} disabled={submitting} data-testid="fuel-submit" className="w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold uppercase text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting && <Loader2 className="animate-spin" size={14}/>}{dict.fieldOps.saveFuel}
        </button>
        {logs.length > 0 && <ul className="space-y-1 text-xs">
            {logs.map(l => <li key={l.id} className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2">
                {l.date}: {l.liters}L · ${l.total_cost_mxn} {l.anomaly_flag && <span className="text-red-300">⚠ {l.anomaly_reason}</span>}
            </li>)}
        </ul>}
    </div>;
}

function ViajesSection({ assets, jobId }) {
    const [assetId, setAssetId] = useState(assets[0]?.asset_id || "");
    const [materials, setMaterials] = useState([]);
    const [list, setList] = useState([]);
    const [form, setForm] = useState({ origin_text:"", destination_text:"", material_id:"", quantity:"", notes:"" });
    const [err, setErr] = useState(""); const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const job = await api.get(`/jobs/${jobId}`).catch(() => null);
                const mats = await api.get("/materials");
                setMaterials(mats.data);
                // Filter to those in job
                const jobMats = job?.data?.material_assignments || [];
                if (jobMats.length) setMaterials(mats.data.filter(m => jobMats.some(jm => jm.material_id === m.id)));
                const r = await api.get(`/jobs/${jobId}/viajes`); setList(r.data);
            } catch {}
        })();
    }, [jobId]);

    const submit = async () => {
        if (!assetId || !form.material_id || !form.quantity) { setErr(dict.fieldOps.viajeRequired); return; }
        setSubmitting(true); setErr("");
        try {
            await api.post(`/jobs/${jobId}/viajes`, { asset_id: assetId, ...form, quantity: parseFloat(form.quantity) });
            setForm({ origin_text:"", destination_text:"", material_id: form.material_id, quantity:"", notes:"" });
            const r = await api.get(`/jobs/${jobId}/viajes`); setList(r.data);
        } catch (e) {
            if (!e?.response) {
                await enqueueViaje({
                    jobId,
                    body: { asset_id: assetId, ...form, quantity: parseFloat(form.quantity) },
                });
                await broadcastPending();
                setForm({ origin_text:"", destination_text:"", material_id: form.material_id, quantity:"", notes:"" });
                setErr(dict.fieldOps.queuedOffline);
            } else {
                setErr(formatApiError(e));
            }
        }
        finally { setSubmitting(false); }
    };

    return <div className="space-y-3" data-testid="viajes-section">
        <select value={assetId} onChange={e=>setAssetId(e.target.value)} className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="viaje-asset">
            {volteoAssets.map(a => <option key={a.asset_id} value={a.asset_id}>{a.asset_nickname}</option>)}
        </select>
        <input value={form.origin_text} onChange={e=>setForm({...form,origin_text:e.target.value})} placeholder={dict.fieldOps.origin} className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="viaje-origin"/>
        <input value={form.destination_text} onChange={e=>setForm({...form,destination_text:e.target.value})} placeholder={dict.fieldOps.destination} className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="viaje-dest"/>
        <select value={form.material_id} onChange={e=>setForm({...form,material_id:e.target.value})} className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="viaje-material">
            <option value="">—</option>
            {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
        </select>
        <input type="number" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})} placeholder={dict.fieldOps.quantity} className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="viaje-qty"/>
        {err && <div className="text-xs text-red-300" data-testid="viaje-err">{err}</div>}
        <button type="button" onClick={submit} disabled={submitting} data-testid="viaje-submit" className="w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold uppercase text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting && <Loader2 className="animate-spin" size={14}/>}+ {dict.fieldOps.addViaje}
        </button>
        <div className="text-xs text-slate-400">{dict.fieldOps.viajesCount}: <span className="text-brand-orange font-mono">{list.length}</span></div>
        {list.length > 0 && <ul className="space-y-1 text-xs">
            {list.map(v => <li key={v.id} className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2">
                #{v.viaje_number}: {v.origin_text} → {v.destination_text} · {v.quantity}
            </li>)}
        </ul>}
    </div>;
}

const fmtMXN = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

function TollsSection({ assets, jobId }) {
    const [list, setList] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [form, setForm] = useState({
        asset_id: assets[0]?.asset_id || "",
        date: new Date().toISOString().slice(0, 10),
        location: "",
        amount_mxn: "",
        notes: "",
    });
    const [file, setFile] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/tolls", { params: { job_id: jobId } });
            setList(r.data);
        } catch { /* silent */ }
    }, [jobId]);
    useEffect(() => { load(); }, [load]);

    const resetForm = () => {
        setForm({
            asset_id: assets[0]?.asset_id || "",
            date: new Date().toISOString().slice(0, 10),
            location: "", amount_mxn: "", notes: "",
        });
        setFile(null);
        setErr("");
    };

    const submit = async () => {
        setErr("");
        const amt = parseFloat(form.amount_mxn);
        if (!form.location.trim()) { setErr(dict.fieldOps.toll.missingFields); return; }
        if (!amt || amt <= 0) { setErr(dict.fieldOps.toll.invalidAmount); return; }
        setSubmitting(true);
        try {
            let photo_id = null;
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("kind", "toll_receipt");
                const rp = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                photo_id = rp.data?.id || null;
            }
            await api.post("/tolls", {
                job_id: jobId,
                asset_id: form.asset_id || null,
                date: form.date,
                location: form.location.trim(),
                amount_mxn: amt,
                photo_id,
                notes: form.notes || null,
            });
            setShowForm(false);
            resetForm();
            load();
        } catch (e) {
            if (!e?.response) {
                await enqueueTollCreate({
                    blob: file || null,
                    filename: file?.name,
                    contentType: file?.type,
                    jobId,
                    assetId: form.asset_id || null,
                    date: form.date,
                    location: form.location.trim(),
                    amount_mxn: amt,
                    notes: form.notes || null,
                });
                await broadcastPending();
                setShowForm(false);
                resetForm();
                setErr(dict.fieldOps.queuedOffline);
            } else {
                setErr(formatApiError(e));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const total = list.reduce((s, t) => s + (t.amount_mxn || 0), 0);
    const nameAsset = (id) => (assets.find(a => a.asset_id === id) || {}).asset_nickname || "—";

    return (
        <div className="space-y-3" data-testid="tolls-section">
            <div className="flex items-center justify-between">
                <div className="text-xs text-slate-400">
                    {dict.fieldOps.toll.registered}:{" "}
                    <span className="text-brand-orange font-mono">{list.length}</span>
                    {list.length > 0 && (
                        <> · {dict.fieldOps.toll.total}: <span className="text-brand-orange font-mono">{fmtMXN(total)}</span></>
                    )}
                </div>
                {!showForm && (
                    <button
                        type="button"
                        onClick={() => { setShowForm(true); setErr(""); }}
                        data-testid="toll-add-btn"
                        className="h-9 px-3 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase text-xs inline-flex items-center gap-1"
                    >
                        <Plus size={14}/>{dict.fieldOps.toll.addToll}
                    </button>
                )}
            </div>

            {showForm && (
                <div className="rounded-xl bg-slate-950/60 border border-brand-orange/40 p-4 space-y-3" data-testid="toll-form">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-brand-orange inline-flex items-center gap-2">
                            <Receipt size={14}/> {dict.fieldOps.toll.formTitle}
                        </div>
                        <button
                            type="button"
                            onClick={() => { setShowForm(false); resetForm(); }}
                            data-testid="toll-form-cancel"
                            className="text-slate-500 hover:text-slate-200"
                        >
                            <X size={16}/>
                        </button>
                    </div>

                    {assets.length > 1 && (
                        <select
                            value={form.asset_id}
                            onChange={e => setForm({ ...form, asset_id: e.target.value })}
                            data-testid="toll-asset"
                            className="w-full h-10 px-3 rounded-lg bg-slate-900 border border-slate-700 text-sm"
                        >
                            {assets.map(a => (
                                <option key={a.asset_id} value={a.asset_id}>{a.asset_nickname}</option>
                            ))}
                        </select>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        <input
                            type="date"
                            value={form.date}
                            onChange={e => setForm({ ...form, date: e.target.value })}
                            data-testid="toll-date"
                            className="h-10 px-3 rounded-lg bg-slate-900 border border-slate-700 text-sm"
                        />
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={dict.fieldOps.toll.amountPh}
                            value={form.amount_mxn}
                            onChange={e => setForm({ ...form, amount_mxn: e.target.value })}
                            data-testid="toll-amount"
                            className="h-10 px-3 rounded-lg bg-slate-900 border border-slate-700 text-sm"
                        />
                    </div>

                    <input
                        type="text"
                        value={form.location}
                        onChange={e => setForm({ ...form, location: e.target.value })}
                        placeholder={dict.fieldOps.toll.locationPh}
                        data-testid="toll-location"
                        className="w-full h-10 px-3 rounded-lg bg-slate-900 border border-slate-700 text-sm"
                    />

                    <textarea
                        value={form.notes}
                        onChange={e => setForm({ ...form, notes: e.target.value })}
                        placeholder={dict.fieldOps.toll.notesPh}
                        data-testid="toll-notes"
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm"
                    />

                    <div className="flex items-center gap-2">
                        <label className="flex-1 inline-flex items-center justify-center gap-1 h-10 rounded-lg border-2 border-dashed border-slate-700 hover:border-brand-orange text-slate-300 text-xs uppercase cursor-pointer">
                            <Camera size={14}/>
                            {file ? file.name.slice(0, 14) + "…" : dict.fieldOps.toll.photoOptional}
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={e => setFile(e.target.files?.[0] || null)}
                                className="hidden"
                                data-testid="toll-photo-input"
                            />
                        </label>
                        {file && (
                            <button
                                type="button"
                                onClick={() => setFile(null)}
                                data-testid="toll-photo-clear"
                                className="h-10 px-2 rounded-lg text-red-400 border border-slate-700"
                            >
                                <X size={14}/>
                            </button>
                        )}
                    </div>

                    {err && <div className="text-xs text-red-300" data-testid="toll-err">{err}</div>}

                    <button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        data-testid="toll-submit"
                        className="w-full h-11 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold uppercase tracking-wider text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                        {submitting && <Loader2 className="animate-spin" size={14}/>}
                        {dict.fieldOps.toll.save}
                    </button>
                </div>
            )}

            {!showForm && err && <div className="text-xs text-red-300" data-testid="toll-err-inline">{err}</div>}

            {list.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-2" data-testid="tolls-empty">{dict.fieldOps.toll.empty}</div>
            ) : (
                <ul className="space-y-1" data-testid="tolls-list">
                    {list.map(t => (
                        <li
                            key={t.id}
                            className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2 text-xs flex items-center justify-between gap-2"
                            data-testid={`toll-item-${t.id}`}
                        >
                            <div className="min-w-0">
                                <div className="text-slate-200 font-medium truncate">{t.location || "—"}</div>
                                <div className="text-slate-500 mt-0.5">
                                    {t.date} {t.asset_id ? `· ${nameAsset(t.asset_id)}` : ""}
                                </div>
                            </div>
                            <div className="text-brand-orange font-mono shrink-0">{fmtMXN(t.amount_mxn)}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}



function IdleToggle({ jobId }) {
    const [idle, setIdle] = useState(false);
    const [reason, setReason] = useState("");
    const [err, setErr] = useState("");

    const toggle = async () => {
        try {
            await api.post(`/jobs/${jobId}/idle`, { idle: !idle, reason: !idle ? reason : null });
            setIdle(!idle); setErr("");
        } catch (e) {
            if (!e?.response) {
                await enqueueIdleToggle({
                    jobId, body: { idle: !idle, reason: !idle ? reason : null },
                });
                await broadcastPending();
                setIdle(!idle); setErr(dict.fieldOps.queuedOffline);
            } else {
                setErr(formatApiError(e));
            }
        }
    };

    return <div className="space-y-2" data-testid="idle-section">
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-950/60 border border-slate-800">
            <div>
                <div className="text-sm font-semibold">{idle ? dict.fieldOps.idleOn : dict.fieldOps.idleOff}</div>
                {idle && reason && <div className="text-xs text-slate-400 mt-1">{reason}</div>}
            </div>
            <button type="button" onClick={toggle} data-testid="idle-toggle" className={`h-10 px-4 rounded-lg font-semibold uppercase text-xs ${idle ? "bg-emerald-600 text-white" : "bg-brand-orange text-slate-950"}`}>
                {idle ? dict.fieldOps.resume : dict.fieldOps.markIdle}
            </button>
        </div>
        {!idle && <input value={reason} onChange={e=>setReason(e.target.value)} placeholder={dict.fieldOps.idleReason} className="w-full h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm" data-testid="idle-reason"/>}
        {err && <div className="text-xs text-red-300">{err}</div>}
    </div>;
}

export default function OperatorRegistro() {
    return (
        <ErrorBoundary description="Ocurrió un error al cargar el registro de campo. Intenta refrescar la página o contacta a soporte.">
            <OperatorRegistroInner/>
        </ErrorBoundary>
    );
}

function OperatorRegistroInner() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [readings, setReadings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        try {
            const [s, r] = await Promise.all([
                api.get(`/jobs/${id}/photos/checklist-status`),
                api.get("/meter-readings", { params: { job_id: id } }),
            ]);
            setStatus(s.data);
            setReadings(r.data);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const closureReady = useMemo(() => {
        // Defensive fallback strings in case the i18n dict is missing keys (e.g. older SW cache).
        const L_before = dict?.fieldOps?.beforeIncomplete || dict?.jobs?.beforeIncomplete || "faltan fotos antes";
        const L_after = dict?.fieldOps?.afterIncomplete || dict?.jobs?.afterIncomplete || "faltan fotos después";
        const L_meter = dict?.fieldOps?.meterMissing || dict?.jobs?.meterMissing || "falta lectura de medidor";
        const assets = status?.assets;
        if (!Array.isArray(assets) || assets.length === 0) return { ok: false, blockers: [] };
        const blockers = [];
        for (const a of assets) {
            const name = a?.asset_nickname || a?.asset_id || "—";
            if (!a?.antes_ready) blockers.push(`${name}: ${L_before}`);
            if (!a?.despues_ready) blockers.push(`${name}: ${L_after}`);
            const has = Array.isArray(readings) && readings.some((r) => r?.asset_id === a?.asset_id);
            if (!has) blockers.push(`${name}: ${L_meter}`);
        }
        return { ok: blockers.length === 0, blockers };
    }, [status, readings]);

    const requestClosure = async () => {
        setSubmitting(true); setErr("");
        try {
            await api.post(`/jobs/${id}/transition`, { to_status: "completado" });
            navigate("/campo");
        } catch (e) { setErr(formatApiError(e)); }
        finally { setSubmitting(false); }
    };

    if (loading) return <div className="px-4 py-10 text-center"><Loader2 className="animate-spin inline text-slate-500"/></div>;
    if (err && !status) return <div className="px-4 py-10 text-center text-red-300">{err}</div>;

    return (
        <div data-testid="operator-registro" className="px-4 py-4 pb-20 space-y-4">
            <Link to={`/campo/trabajos/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100">
                <ArrowLeft size={14}/>{dict.common.back}
            </Link>
            <header>
                <h1 className="font-display text-2xl font-extrabold tracking-tight">{dict.fieldOps.title}</h1>
                <p className="text-xs text-slate-500 mt-1">{dict.fieldOps.subtitle}</p>
            </header>

            {/* A — Fotos antes */}
            <Section title={dict.fieldOps.sectionA} defaultOpen={true}>
                {status?.assets?.map((a) => (
                    <PhotoSection key={`antes-${a.asset_id}`} asset={a} moment="antes" jobId={id} onChange={load}/>
                ))}
            </Section>

            {/* B — Lecturas de medidor */}
            <Section title={dict.fieldOps.sectionB} defaultOpen={true}>
                {status?.assets?.map((a) => (
                    <MeterCard key={`m-${a.asset_id}`} asset={a} jobId={id} onChange={load}/>
                ))}
            </Section>

            {/* C — Combustible */}
            <Section title={dict.fieldOps.sectionC} defaultOpen={false}>
                <FuelSection assets={status?.assets || []} jobId={id}/>
            </Section>

            {/* H — Casetas (colocada justo después de Combustible para flujo natural) */}
            <Section title={dict.fieldOps.sectionH} defaultOpen={false}>
                <TollsSection assets={status?.assets || []} jobId={id}/>
            </Section>

            {/* D — Viajes (solo si algún asset tiene subtype volteo) */}
            {(status?.assets || []).some(a => (a.asset_subtype || "").trim().toLowerCase() === "volteo") && (
                <Section title={dict.fieldOps.sectionD} defaultOpen={false}>
                    <ViajesSection assets={status?.assets || []} jobId={id}/>
                </Section>
            )}

            {/* E — Idle */}
            <Section title={dict.fieldOps.sectionE} defaultOpen={false}>
                <IdleToggle jobId={id}/>
            </Section>

            {/* F — Fotos despues */}
            <Section title={dict.fieldOps.sectionF} defaultOpen={false}>
                {status?.assets?.map((a) => (
                    <PhotoSection key={`despues-${a.asset_id}`} asset={a} moment="despues" jobId={id} onChange={load}/>
                ))}
            </Section>

            {/* G — Solicitar cierre */}
            <Section title={dict.fieldOps.sectionG} defaultOpen={true}>
                {!closureReady.ok && (
                    <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-3 text-sm text-red-200 space-y-1" data-testid="closure-blockers">
                        <div className="flex items-center gap-2 font-semibold"><AlertTriangle size={14}/>{dict.fieldOps.missing}</div>
                        <ul className="list-disc list-inside text-xs">
                            {closureReady.blockers.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                    </div>
                )}
                <button
                    type="button"
                    onClick={requestClosure}
                    disabled={!closureReady.ok || submitting}
                    data-testid="request-closure-btn"
                    className="w-full min-h-[56px] rounded-2xl bg-brand-orange hover:bg-orange-400 text-slate-950 font-extrabold uppercase tracking-wider disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition shadow-[0_4px_0_rgba(0,0,0,0.4)] active:translate-y-[2px] active:shadow-[0_2px_0_rgba(0,0,0,0.4)]"
                >
                    {submitting && <Loader2 className="animate-spin" size={18}/>}
                    {dict.fieldOps.requestClosure}
                </button>
                {err && <div className="text-xs text-red-300 mt-2" data-testid="closure-err">{err}</div>}
            </Section>
        </div>
    );
}
